const parseNotificationPayload = (notification) => {
  if (!notification) return {};

  const { data } = notification;
  if (data && typeof data === 'object') {
    return data;
  }

  if (data && typeof data === 'string' && data !== 'undefined') {
    try {
      return JSON.parse(data);
    } catch (error) {
      console.error('❌ Error parsing medical notification payload:', error);
    }
  }

  return notification;
};

const getDeviceKeyFromNotification = (notification, payload) => {
  return payload?.deviceKey ||
    payload?.device_key ||
    notification?.deviceKey ||
    notification?.device_key ||
    null;
};

const normalizeSender = (notification, payload) => {
  if (payload?.sender) {
    return payload.sender;
  }

  if (notification?.sender) {
    return notification.sender;
  }

  return {
    id: payload?.sender_id || notification?.sender_id || payload?.uid || notification?.uid || null,
    name: payload?.sender_name || notification?.sender_name || 'Medical Professional',
    image: payload?.sender_image || notification?.sender_image || null
  };
};

const extractMediaFromMessageContent = (messageContent) => {
  if (typeof messageContent !== 'string') {
    return { content: messageContent, media: null };
  }

  const trimmed = messageContent.trim();
  if (!trimmed.startsWith('{')) {
    return { content: messageContent, media: null };
  }

  try {
    const parsedMessage = JSON.parse(trimmed);
    if (parsedMessage && Array.isArray(parsedMessage.media)) {
      return {
        content: null,
        media: parsedMessage.media
      };
    }
  } catch (error) {
    console.warn('⚠️ Failed to parse media JSON in medical notification message:', error);
  }

  return { content: messageContent, media: null };
};

const normalizeMediaItems = (media = []) => {
  if (!Array.isArray(media)) {
    return [];
  }

  return media.map((item) => {
    const normalized = { ...item };
    if (!normalized.file_type) {
      if (normalized.is_image) {
        normalized.file_type = 'image';
      } else if (normalized.is_video) {
        normalized.file_type = 'video';
      } else if (normalized.is_document) {
        normalized.file_type = 'document';
      } else if (normalized.is_audio) {
        normalized.file_type = 'audio';
      } else {
        normalized.file_type = 'file';
      }
    }
    return normalized;
  });
};

const extractConversationId = (payload, notification, sender) => {
  const fromActionUrl = (url) => {
    if (!url || typeof url !== 'string') return null;
    const chatSegment = url.split('/chat/')[1];
    if (chatSegment) {
      return chatSegment.split('/')[0];
    }
    const clinicSegment = url.split('/medical/clinic/')[1];
    if (clinicSegment) {
      return clinicSegment.split('/')[0];
    }
    return null;
  };

  return payload?.conversation_id ||
    notification?.conversation_id ||
    payload?.target_id ||
    notification?.target_id ||
    sender?.id ||
    fromActionUrl(payload?.action_url) ||
    fromActionUrl(notification?.action_url) ||
    null;
};

const isConversationOpen = (conversationId) => {
  if (typeof window === 'undefined' || !conversationId) {
    return false;
  }

  const currentPath = window.location.pathname;
  const activeConversationId = localStorage.getItem('active_conversation_id');

  const isOpenInPath = currentPath === `/chat/${conversationId}` ||
    currentPath.includes(`/chat/${conversationId}/`) ||
    currentPath.includes(`chat/${conversationId}`) ||
    currentPath.includes(`/medical/clinic/${conversationId}`) ||
    currentPath.includes(`/medical/clinic/chat/${conversationId}`);

  if (isOpenInPath) {
    return true;
  }

  if (!activeConversationId) {
    return false;
  }

  return activeConversationId.toString() === conversationId.toString();
};

const buildMediaSummary = (media) => {
  if (!media || media.length === 0) {
    return null;
  }

  const counts = {
    image: media.filter((item) => item.is_image || item.file_type === 'image').length,
    video: media.filter((item) => item.is_video || item.file_type === 'video').length,
    document: media.filter((item) => item.is_document || item.file_type === 'document').length
  };

  const parts = [];
  if (counts.image > 0) parts.push(`${counts.image} image${counts.image > 1 ? 's' : ''}`);
  if (counts.video > 0) parts.push(`${counts.video} video${counts.video > 1 ? 's' : ''}`);
  if (counts.document > 0) parts.push(`${counts.document} document${counts.document > 1 ? 's' : ''}`);

  if (parts.length === 0) {
    return `Sent ${media.length} file${media.length > 1 ? 's' : ''}`;
  }

  return `Sent ${parts.join(', ')}`;
};

const createMedicalToastMessage = (messageContent, media, payload) => {
  if (messageContent) {
    return messageContent;
  }

  if (media.length > 0) {
    const mediaTypes = media
      .map((item) => item.file_type)
      .filter((type, index, array) => array.indexOf(type) === index);

    if (mediaTypes.length === 1) {
      const [type] = mediaTypes;
      const count = media.length;

      if (type === 'image') {
        return count === 1 ? '🏥 Medical Photo' : `🏥 ${count} Medical Photos`;
      }

      if (type === 'video') {
        return count === 1 ? '🏥 Medical Video' : `🏥 ${count} Medical Videos`;
      }

      if (type === 'document') {
        return count === 1 ? '🏥 Medical Document' : `🏥 ${count} Medical Documents`;
      }

      if (type === 'audio') {
        return count === 1 ? '🏥 Medical Voice message' : `🏥 ${count} Medical Voice messages`;
      }

      return count === 1 ? '🏥 Medical File' : `🏥 ${count} Medical Files`;
    }

    return `🏥 ${media.length} Medical Files`;
  }

  if (payload?.content && !Number.isNaN(parseInt(payload.content, 10))) {
    const parsedValue = parseInt(payload.content, 10);
    return `You have ${parsedValue} new medical message${parsedValue > 1 ? 's' : ''}`;
  }

  return 'New medical message received';
};

const getMediaPreview = (media) => {
  if (!media || media.length === 0) {
    return null;
  }

  const [firstItem] = media;
  return {
    url: firstItem.url,
    type: firstItem.file_type,
    isImage: firstItem.is_image,
    isVideo: firstItem.is_video,
    name: firstItem.file_name
  };
};

const navigateToMedicalChat = (navigate, actionUrl, conversationId) => {
  if (!navigate) return;

  if (actionUrl) {
    const target = actionUrl.startsWith('/') ? actionUrl : `/${actionUrl}`;
    navigate(target);
    return;
  }

  if (conversationId) {
    navigate(`/medical/clinic/${conversationId}`);
    return;
  }

  navigate('/medical/clinic');
};

const dispatchMedicalBadgeUpdate = (count, detail) => {
  const badgeUpdateEvent = new CustomEvent('medical-chat-badge-update', {
    detail: {
      ...detail,
      count,
      timestamp: new Date().toISOString(),
      conversationType: 'medical'
    }
  });
  window.dispatchEvent(badgeUpdateEvent);
};

const dispatchMedicalChatListUpdate = (detail) => {
  const updateChatListEvent = new CustomEvent('update-chat-list-direct', {
    detail: {
      ...detail,
      conversationType: 'medical',
      conversation_type: 'medical',
      timestamp: new Date().toISOString()
    }
  });
  window.dispatchEvent(updateChatListEvent);
};

const dispatchMedicalConversationEvent = (detail) => {
  const event = new CustomEvent('medical-conversation-notification', {
    detail: {
      ...detail,
      conversation_type: 'medical'
    }
  });
  window.dispatchEvent(event);
};

const dispatchMedicalMessageUpdateEvent = (detail) => {
  const event = new CustomEvent('new-message-received', {
    detail: {
      ...detail,
      conversationType: 'medical',
      timestamp: new Date().toISOString()
    }
  });
  window.dispatchEvent(event);
};

const dispatchMedicalMessageSyncEvent = (detail) => {
  const event = new CustomEvent('message-sent-sync', {
    detail: {
      ...detail,
      conversationType: 'medical',
      timestamp: new Date().toISOString()
    }
  });
  window.dispatchEvent(event);
};

const dispatchMedicalRefreshEvent = (detail) => {
  const event = new CustomEvent('refresh-chat-messages', {
    detail: {
      ...detail,
      conversationType: 'medical'
    }
  });
  window.dispatchEvent(event);
};

const showBrowserNotificationForSender = ({ notificationData, notificationBody, conversationId, isPermissionGranted, navigate }) => {
  if (!isPermissionGranted || typeof Notification === 'undefined' || Notification.permission !== 'granted') {
    return;
  }

  const notificationTitle = notificationData.title || 'Medical message sent';
  const notificationIcon = notificationData.sender_image || notificationData.image || '/favicon.ico';

  const browserNotification = new Notification(notificationTitle, {
    body: notificationBody,
    icon: notificationIcon,
    badge: '/favicon.ico',
    tag: `medical-message-sent-${conversationId}-${Date.now()}`,
    data: {
      ...notificationData,
      conversation_id: conversationId,
      isSenderNotification: true
    }
  });

  browserNotification.onclick = () => {
    window.focus();
    if (notificationData.action_url) {
      navigate(notificationData.action_url);
    }
    browserNotification.close();
  };
};

const handleMedicalSenderFlow = ({
  notificationData,
  payload,
  isPermissionGranted,
  navigate
}) => {
  const sender = normalizeSender(notificationData, payload);
  const { media, messageContent } = (() => {
    const { content, media: mediaOverride } = extractMediaFromMessageContent(
      payload.message ||
      payload.content ||
      notificationData.content ||
      notificationData.message ||
      null
    );

    const normalizedMedia = normalizeMediaItems(
      mediaOverride || payload.media || notificationData.media || []
    );

    return {
      media: normalizedMedia,
      messageContent: content
    };
  })();

  const conversationId = extractConversationId(payload, notificationData, sender);
  if (!conversationId) {
    console.warn('⚠️ Unable to determine medical conversation id for sender notification');
    return;
  }

  const notificationBody = messageContent || buildMediaSummary(media) || 'Your message was sent';
  const conversationOpen = isConversationOpen(conversationId);

  dispatchMedicalMessageSyncEvent({
    type: 'message_received',
    conversationId,
    message: messageContent,
    content: notificationBody,
    media,
    isSenderNotification: true,
    deviceKey: getDeviceKeyFromNotification(notificationData, payload),
    notificationData
  });

  if (conversationOpen) {
    dispatchMedicalRefreshEvent({
      conversationId,
      message: messageContent,
      content: notificationBody,
      media,
      isSenderNotification: true
    });
    return;
  }

  showBrowserNotificationForSender({
    notificationData,
    notificationBody,
    conversationId,
    isPermissionGranted,
    navigate
  });
};

const handleMedicalReceiverFlow = ({
  notificationData,
  payload,
  navigate,
  showNotificationToast
}) => {
  const sender = normalizeSender(notificationData, payload);
  const { media, hasMedia, messageContent } = (() => {
    const rawMessage = payload.message ||
      payload.content ||
      notificationData.content ||
      notificationData.message ||
      null;
    const { content, media: mediaOverride } = extractMediaFromMessageContent(rawMessage);
    const normalizedMedia = normalizeMediaItems(
      mediaOverride || payload.media || notificationData.media || []
    );

    return {
      messageContent: content,
      media: normalizedMedia,
      hasMedia: normalizedMedia.length > 0
    };
  })();

  const conversationId = extractConversationId(payload, notificationData, sender);
  const conversationOpen = isConversationOpen(conversationId);

  const currentBadgeCount = parseInt(localStorage.getItem('medical_chat_badge_count') || '0', 10);
  let newChatCount;
  if (payload.unread_count !== undefined && payload.unread_count !== null) {
    newChatCount = payload.unread_count;
  } else if (notificationData.content && !Number.isNaN(parseInt(notificationData.content, 10))) {
    newChatCount = parseInt(notificationData.content, 10);
  } else {
    newChatCount = currentBadgeCount + 1;
  }

  if (newChatCount !== null && newChatCount !== undefined) {
    localStorage.setItem('medical_chat_badge_count', newChatCount.toString());
  }

  dispatchMedicalBadgeUpdate(newChatCount, {
    message: messageContent,
    sender,
    media,
    hasMedia
  });

  dispatchMedicalChatListUpdate({
    senderId: sender?.id || payload.sender_id || notificationData.sender_id,
    conversationId,
    conversation_id: conversationId,
    message: messageContent,
    content: messageContent,
    media: media || [],
    sender
  });

  const isOnChatPage = typeof window !== 'undefined' && window.location.pathname.includes('/chat');
  if (isOnChatPage) {
    dispatchMedicalConversationEvent({
      ...notificationData,
      ...payload,
      type: 'message_received',
      conversation_id: conversationId,
      content: messageContent,
      message: messageContent,
      sender_id: sender.id,
      sender_name: sender.name,
      sender_image: sender.image,
      media,
      hasMedia,
      messageId: payload.message_id || notificationData.message_id || payload.id || notificationData.id,
      created_at: notificationData.created_at || new Date().toISOString(),
      is_sender_notification: false
    });
  }

  if (conversationOpen) {
    dispatchMedicalMessageUpdateEvent({
      id: payload.message_id || notificationData.message_id || payload.id || notificationData.id,
      message: messageContent,
      content: messageContent,
      sender,
      senderId: sender?.id || payload.sender_id,
      media,
      hasMedia,
      conversationId,
      messageId: payload.message_id || notificationData.message_id || payload.id || notificationData.id,
      mediaPreview: getMediaPreview(media)
    });
    return;
  }

  const notificationMessage = createMedicalToastMessage(messageContent, media, payload);

  const toastOptions = {
    type: 'info',
    title: payload.title || `🏥 ${sender?.name || 'Medical Professional'}`,
    message: notificationMessage,
    image: sender?.image || null,
    duration: 10000,
    clickable: false
  };

  const mediaPreview = getMediaPreview(media);
  if (mediaPreview) {
    toastOptions.mediaPreview = mediaPreview;
  }

  showNotificationToast(payload, toastOptions);
};

const shouldSkipForDevice = (notification, payload) => {
  const currentDevice = localStorage.getItem('device_id');
  if (!currentDevice) {
    return false;
  }

  const notificationDeviceKey = getDeviceKeyFromNotification(notification, payload);
  if (!notificationDeviceKey) {
    return false;
  }

  return notificationDeviceKey.toString() === currentDevice.toString();
};

const handleMedicalMessageSentSync = ({
  notificationData,
  payload
}) => {
  const sender = normalizeSender(notificationData, payload);
  const { media, messageContent } = (() => {
    const rawMessage = payload.message ||
      payload.content ||
      notificationData.content ||
      notificationData.message ||
      null;
    const { content, media: mediaOverride } = extractMediaFromMessageContent(rawMessage);
    const normalizedMedia = normalizeMediaItems(
      mediaOverride || payload.media || notificationData.media || []
    );

    return {
      messageContent: content,
      media: normalizedMedia
    };
  })();

  const conversationId = extractConversationId(payload, notificationData, sender);
  const conversationOpen = isConversationOpen(conversationId);
  const notificationBody = messageContent || buildMediaSummary(media) || 'Your message was sent';

  dispatchMedicalChatListUpdate({
    senderId: sender?.id || payload.sender_id || notificationData.sender_id,
    conversationId,
    conversation_id: conversationId,
    message: messageContent,
    content: notificationBody,
    media,
    sender
  });

  const isOnChatPage = typeof window !== 'undefined' &&
    (window.location.pathname.includes('/chat') || window.location.pathname.includes('/medical/clinic/chat'));

  if (isOnChatPage) {
    dispatchMedicalConversationEvent({
      ...notificationData,
      ...payload,
      type: 'message_received',
      conversation_id: conversationId,
      content: messageContent,
      message: messageContent,
      sender_id: sender.id,
      sender_name: sender.name,
      sender_image: sender.image,
      media,
      hasMedia: media.length > 0,
      messageId: payload.message_id || notificationData.message_id || payload.id || notificationData.id,
      created_at: notificationData.created_at || new Date().toISOString(),
      is_sender_notification: true
    });
  }

  if (conversationOpen) {
    dispatchMedicalMessageUpdateEvent({
      id: payload.message_id || notificationData.message_id || payload.id || notificationData.id,
      message: messageContent,
      content: messageContent,
      sender,
      senderId: sender?.id || payload.sender_id,
      media,
      hasMedia: media.length > 0,
      conversationId,
      messageId: payload.message_id || notificationData.message_id || payload.id || notificationData.id,
      mediaPreview: getMediaPreview(media),
      is_sender_notification: true
    });
  }

  dispatchMedicalMessageSyncEvent({
    type: 'message_received',
    conversationId,
    message: messageContent,
    content: notificationBody,
    media,
    isSenderNotification: true,
    deviceKey: getDeviceKeyFromNotification(notificationData, payload),
    notificationData
  });
};

export const handleMedicalMessageReceivedNotification = ({
  notificationData,
  isPermissionGranted,
  navigate,
  showNotificationToast
}) => {
  try {
    const payload = parseNotificationPayload(notificationData);
    const isSenderNotification = notificationData.is_sender_notification === true;

    if (isSenderNotification) {
      if (shouldSkipForDevice(notificationData, payload)) {
        return;
      }

      handleMedicalSenderFlow({
        notificationData,
        payload,
        isPermissionGranted,
        navigate
      });
      return;
    }

    handleMedicalReceiverFlow({
      notificationData,
      payload,
      navigate,
      showNotificationToast
    });
  } catch (error) {
    console.error('❌ Error handling medical conversation notification:', error);
  }
};

export const handleMedicalMessageSentFromMySideNotification = ({
  notificationData
}) => {
  try {
    const payload = parseNotificationPayload(notificationData);
    if (shouldSkipForDevice(notificationData, payload)) {
      return;
    }

    handleMedicalMessageSentSync({
      notificationData,
      payload
    });
  } catch (error) {
    console.error('❌ Error handling medical message_sent_from_my_side notification:', error);
  }
};

export const isMedicalConversationNotification = (notificationData) => {
  if (!notificationData) {
    return false;
  }

  if (notificationData.type === 'new_message_received_in_medical_conversation') {
    return true;
  }

  if (notificationData.conversation_type === 'medical') {
    return true;
  }

  const { data } = notificationData;
  if (!data) {
    return false;
  }

  if (typeof data === 'object' && data.conversation_type === 'medical') {
    return true;
  }

  if (typeof data === 'string' && data !== 'undefined') {
    try {
      const parsed = JSON.parse(data);
      if (parsed?.conversation_type === 'medical') {
        return true;
      }
    } catch (error) {
      console.warn('⚠️ Unable to parse medical notification conversation type:', error);
    }
  }

  return false;
};

